import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnomalyIncident } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncident';
import { AnomalyMetricKey, AnomalyMetricSemantics } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import {
  buildAnomalyPeriodLabel,
  formatAnomalyContributorValue,
  formatAnomalyDeltaValue,
  getAnomalyAccentColor,
  getAnomalyContributorIconClass,
  getAnomalyDirectionIconClass,
  getAnomalyModelLabel,
  sortAnomalyModels,
  sortAnomalyPrompts,
} from '../../../../../../../shared/helpers/AnomalyIncidentHelpers';
import {
  getAnomalyMetricLabel,
  getAnomalyMetricVerb,
} from './anomalyMetricConfig';

export interface AnomalySingleMetricTooltipProps {
  dateKey: string;
  incident: AnomalyIncident;
  metric: AnomalyMetricKey;
  metricSemantics: AnomalyMetricSemantics;
  valueUnit?: string;
  dateFormatter?: (raw: string) => string;
}

interface ContributorRowProps {
  label: string;
  type: AnomalyIncident['topContributorPrompts'][number]['type'];
  value: number;
  unit: string;
  direction: AnomalyIncident['direction'];
  accent: string;
}

const ContributorRow: React.FC<ContributorRowProps> = ({
  label,
  type,
  value,
  unit,
  direction,
  accent,
}) => {
  const iconClass = getAnomalyContributorIconClass(type);
  const valueLabel = formatAnomalyContributorValue(type, value, unit, direction);

  return (
    <span className="anomaly-tooltip__contrib-row">
      <span className={`ico ico--xs ${iconClass}`} style={{ backgroundColor: accent }} aria-hidden="true" />
      <span className="anomaly-tooltip__contrib-label">{label}</span>
      <strong className="anomaly-tooltip__contrib-value">({valueLabel})</strong>
    </span>
  );
};

const AnomalyMarkerSingleMetricTooltip: React.FC<AnomalySingleMetricTooltipProps> = ({
  dateKey,
  incident,
  metric,
  metricSemantics,
  valueUnit = '',
  dateFormatter,
}) => {
  const { t } = useTranslation();
  const accent = getAnomalyAccentColor(incident.direction);
  const directionIconClass = getAnomalyDirectionIconClass(incident.direction);
  const periodLabel = buildAnomalyPeriodLabel(incident, dateKey, dateFormatter);

  const metricName = getAnomalyMetricLabel(t, metric);
  const verb = getAnomalyMetricVerb(t, metric, incident.direction);

  const formatVal = (value: number): string => `${value}${valueUnit}`;
  const currentVal = incident.currentValue != null ? formatVal(incident.currentValue) : '–';
  const baselineVal = incident.baselineValue != null ? formatVal(incident.baselineValue) : '–';
  const delta =
    incident.currentValue != null && incident.baselineValue != null
      ? `${formatAnomalyDeltaValue(
          incident.currentValue - incident.baselineValue,
          incident.direction,
        )}${valueUnit}`
      : null;

  const headline = `${metricName} ${verb} ${baselineVal} ${t('anomalyHeadlineTo')} ${currentVal}`;

  const promptItems = incident.direction
    ? sortAnomalyPrompts(
        incident.topContributorPrompts,
        metricSemantics.increaseIsPositive,
        incident.direction,
      )
    : [];
  const modelItems = incident.direction
    ? sortAnomalyModels(
        incident.topContributorModels,
        metricSemantics.increaseIsPositive,
        incident.direction,
      )
    : [];

  return (
    <div className="anomaly-tooltip">
      <div className="anomaly-tooltip__accent" style={{ background: accent }} />
      <div className="anomaly-tooltip__header">
        <div className="anomaly-tooltip__direction">
          <span className="anomaly-tooltip__direction-icon">
            {directionIconClass ? (
              <span className={`ico ico--xs ${directionIconClass}`} aria-hidden="true" />
            ) : (
              '–'
            )}
          </span>
          <span className="anomaly-tooltip__direction-label" style={{ color: accent }}>
            {delta ?? '–'}
          </span>
        </div>
        <span className="anomaly-tooltip__date">{periodLabel}</span>
      </div>
      <div className="anomaly-tooltip__body">
        <p className="anomaly-tooltip__headline">{headline}</p>
        {promptItems.length > 0 && (
          <div className="anomaly-tooltip__section">
            <div className="anomaly-tooltip__section-label">{t('anomalyTooltipTopPrompts')}</div>
            <ul className="anomaly-tooltip__list">
              {promptItems.map((prompt) => (
                <li key={`${prompt.monitorId}-${prompt.prompt}`} className="anomaly-tooltip__list-item">
                  <ContributorRow
                    label={prompt.prompt}
                    type={prompt.type}
                    value={prompt.value}
                    unit={valueUnit}
                    direction={incident.direction}
                    accent={accent}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        {modelItems.length > 0 && (
          <div className="anomaly-tooltip__section">
            <div className="anomaly-tooltip__section-label">{t('anomalyTooltipTopModels')}</div>
            <ul className="anomaly-tooltip__list">
              {modelItems.map((model) => (
                <li key={model.model} className="anomaly-tooltip__list-item">
                  <ContributorRow
                    label={getAnomalyModelLabel(model.model)}
                    type={model.type}
                    value={model.value}
                    unit={valueUnit}
                    direction={incident.direction}
                    accent={accent}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnomalyMarkerSingleMetricTooltip;
